import { CustomEnumeration } from './custom-enum.class';
import { EnumerationItem } from './enum-item.class';
import { FieldInstance } from './field.service';
import { StaticEnumeration } from './static-enum.class';
import { dependencies } from './workflow.dependency';
import { PrimitiveType, WorkflowEnum } from './workflow.type';

export interface WorkflowEnumeration {
  values: EnumerationItem[];
  isSameEnum(instances: { [name: string]: Enumeration }): boolean;
}

/**
 * Enum specific logic
 */
export class Enumeration {
  /**
   * Enumerations by name
   */
  private static readonly instances: { [name: string]: Enumeration } = {};

  /**
   * actual implementation of the work
   */
  private readonly enumerationImp: WorkflowEnumeration;

  /**
   * Field config as specified at `workflow-enums.json` (object) or `api-enums.json` (array)
   */
  public readonly config: WorkflowEnum | PrimitiveType[];

  /**
   * Array of enum values
   */
  public get values(): EnumerationItem[] {
    return this.enumerationImp.values;
  }

  /**
   * Get a unique Enumeration as a flat structure
   * @param id config id (as set in `workflow-enums.json`)
   */
  constructor(public readonly id: string, gracefulFail?: boolean, field?: FieldInstance) {
    this.config = dependencies.config.enums[id] || dependencies.config.apiEnums[id];

    if (!this.config) {
      throw new Error(`No config has been found for Enum id '${id}'`);
    }

    if (this.isCustomEnum && !field) {
      throw new Error(`Enum ${id} is a custom enum and cannot be used in storage config.`);
    }

    this.id = !this.isCustomEnum ? id : `${this.id}_${field.id}`;
    this.validateConfig(gracefulFail);
    // enforcing unique instances per id
    if (Enumeration.instances[this.id]?.isSameEnum) {
      return Enumeration.instances[this.id];
    }

    Enumeration.instances[this.id] = this;
    this.enumerationImp = this.buildEnumeration(field, gracefulFail);
  }

  /**
   * Check if the enum config is a custom enum
   */
  private get isCustomEnum(): boolean {
    if (Array.isArray(this.config)) {
      return false;
    }
    return !!this.config.custom;
  }

  /**
   * check the uniqueness of enum based on actual implementation of WorkflowEnumeration
   */
  private get isSameEnum(): boolean {
    return this.enumerationImp?.isSameEnum(Enumeration.instances);
  }

  private buildEnumeration(field: FieldInstance, gracefulFail?: boolean): WorkflowEnumeration {
    if (this.isCustomEnum) {
      return new CustomEnumeration(this.id, (this.config as WorkflowEnum).custom, field, gracefulFail);
    }
    return new StaticEnumeration(this.id, this.config, Enumeration, gracefulFail);
  }

  private validateConfig(gracefulFail?: boolean): void {
    if ((this.config && Array.isArray(this.config)) || typeof this.config === 'object') {
      return;
    }
    if (!gracefulFail) {
      throw new Error(`Could not find enum "${this.id}"`);
    }
  }
}
